/**
 * Minimal declarative SVG chart for journey steps (line + bar series), styled to
 * match the focused charts in the experience mockup (screen 2 / 4). Auto-scales
 * to the union of all series; supports a baseline at zero when values cross it.
 */

import { useMemo } from 'react'
import type { JourneyChartSpec } from './types'

const VIEW_W = 360
const VIEW_H = 168
const PAD_TOP = 12
const PAD_BOTTOM = 26
const PAD_X = 14

export function JourneyChart({ spec }: { spec: JourneyChartSpec }) {
  const geometry = useMemo(() => {
    const labelCount = Math.max(spec.labels.length, 1)
    const allValues: number[] = []
    for (const s of spec.series) {
      for (const v of s.values) if (v != null && Number.isFinite(v)) allValues.push(v)
    }
    if (allValues.length === 0) return null

    let min = Math.min(...allValues)
    let max = Math.max(...allValues)
    if (min > 0) min = 0
    if (max < 0) max = 0
    if (min === max) {
      min -= 1
      max += 1
    }
    const span = max - min
    const plotW = VIEW_W - PAD_X * 2
    const plotH = VIEW_H - PAD_TOP - PAD_BOTTOM
    const stepX = labelCount > 1 ? plotW / (labelCount - 1) : 0
    const slotW = plotW / labelCount

    const xLine = (i: number) => PAD_X + (labelCount > 1 ? i * stepX : plotW / 2)
    const xBarLeft = (i: number) => PAD_X + i * slotW
    const y = (v: number) => PAD_TOP + (1 - (v - min) / span) * plotH
    const yZero = y(0)

    return { min, max, plotW, plotH, slotW, xLine, xBarLeft, y, yZero, labelCount }
  }, [spec])

  if (!geometry) {
    return (
      <div className="fg-journey-chart-empty" role="img" aria-label="No chart data for this step">
        No chart data for this step yet.
      </div>
    )
  }

  const { xLine, xBarLeft, y, yZero, slotW } = geometry
  const barSeries = spec.series.filter((s) => s.kind === 'bars')
  const lineSeries = spec.series.filter((s) => s.kind === 'line')
  const barGroupW = barSeries.length > 0 ? Math.min(slotW * 0.6, 34) : 0
  const barW = barSeries.length > 0 ? barGroupW / barSeries.length : 0

  return (
    <div className="fg-journey-chart">
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" height={VIEW_H} preserveAspectRatio="none" role="img">
        <line x1={0} y1={yZero} x2={VIEW_W} y2={yZero} stroke="#e7eaf0" />
        {barSeries.map((s, si) =>
          s.values.map((v, i) => {
            if (v == null || !Number.isFinite(v)) return null
            const top = Math.min(y(v), yZero)
            const height = Math.abs(y(v) - yZero)
            const x = xBarLeft(i) + (slotW - barGroupW) / 2 + si * barW
            return (
              <rect
                key={`${s.label}-${i}`}
                x={x}
                y={top}
                width={Math.max(barW - 2, 2)}
                height={Math.max(height, 1)}
                fill={s.color}
                opacity={0.9}
                rx={1.5}
              />
            )
          }),
        )}
        {lineSeries.map((s) => {
          const points = s.values
            .map((v, i) => (v == null || !Number.isFinite(v) ? null : `${xLine(i).toFixed(1)},${y(v).toFixed(2)}`))
            .filter((p): p is string => p != null)
          if (points.length < 2) return null
          return (
            <polyline key={s.label} fill="none" stroke={s.color} strokeWidth={3} points={points.join(' ')} />
          )
        })}
        <g fontSize={9} fill="#6b7686" textAnchor="middle">
          {spec.labels.map((label, i) => (
            <text key={`${label}-${i}`} x={xLine(i)} y={VIEW_H - 8}>
              {label}
            </text>
          ))}
        </g>
      </svg>
      <div className="fg-journey-legend">
        {spec.series.map((s) => (
          <span key={s.label}>
            <i style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export default JourneyChart
